import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetPrograms200, GetProgramsQueryParams } from '@eureca/recruiter-api-sdk/types';
import type { ProgramsSearch } from './programs-search.schema';

export type ProgramListEntry = GetPrograms200['items'][number];
export type ProgramListResponse = GetPrograms200;

/**
 * Error surfaced when the BFF refuses the request (auth/role/upstream
 * failures). `status` lets the UI branch — `403` renders the no-access
 * fallback; everything else falls into the generic retry block.
 */
export class ProgramsFetchError extends Error {
  readonly name = 'ProgramsFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * Build the query params the BFF expects. Drops `search` when below 2 chars
 * (the BFF would 400 anyway; this saves the round-trip) and undefined fields.
 */
function paramsFromSearch(search: ProgramsSearch, pageOverride?: number): GetProgramsQueryParams {
  const effectiveSearch = search.search && search.search.length >= 2 ? search.search : undefined;
  return {
    ...(search.companyId ? { companyId: search.companyId } : {}),
    ...(effectiveSearch ? { search: effectiveSearch } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    dir: search.dir,
    page: pageOverride ?? search.page,
    pageSize: search.pageSize,
  };
}

export function programsQueryKey(search: ProgramsSearch) {
  return ['recruiter', 'programs', 'page', paramsFromSearch(search)] as const;
}

async function fetchPrograms(
  params: GetProgramsQueryParams,
  signal?: AbortSignal
): Promise<ProgramListResponse> {
  const res = await fetchClient<ProgramListResponse>({
    method: 'GET',
    url: '/programs',
    params,
    signal,
  });
  if (res.error) {
    throw new ProgramsFetchError(
      `Failed to load programs (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

export function programsQueryOptions(search: ProgramsSearch) {
  const params = paramsFromSearch(search);
  return queryOptions({
    queryKey: programsQueryKey(search),
    queryFn: ({ signal }) => fetchPrograms(params, signal),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => {
      if (err instanceof ProgramsFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
